import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { MainLayout } from '../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { 
  Award, 
  ChevronRight,
  BarChart3,
  Video,
  Mic,
  Lock,
  Settings,
  History,
  TrendingUp,
  MessageSquare
} from 'lucide-react';

const CommunicationTraining = () => {
  const { api, user, isAdmin } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    evolve: { sessions: 0, avgScore: 0 }
  });

  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);
      
      const results = await Promise.allSettled([
        api.get('/evolve/analytics/summary')
      ]);
      
      const [evolveRes] = results;
      
      setStats({
        evolve: {
          sessions: evolveRes.value?.data?.total_sessions || 0,
          avgScore: evolveRes.value?.data?.average_score || 0
        }
      });
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // Communication Training modules
  const communicationModules = [
    {
      id: 'evolve-ai',
      title: 'Evolve AI',
      description: 'AI-powered presentation training with real-time feedback on delivery, content, and engagement. Practice and perfect your presentation skills.',
      icon: Video,
      color: 'from-orange-500 to-amber-600',
      bgColor: 'bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-950/30 dark:to-amber-950/30',
      borderColor: 'border-orange-200 dark:border-orange-800',
      path: '/evolve',
      statsLabel: 'Sessions',
      statsValue: stats.evolve.sessions,
      progress: null,
      features: ['Live Presentations', 'AI Feedback', 'Voice Analysis', 'Slide Review', 'Performance Tracking'],
      badge: stats.evolve.avgScore >= 80 ? `Avg: ${stats.evolve.avgScore}%` : null,
      enabled: true
    },
    {
      id: 'communication-coach',
      title: 'Communication Coach',
      description: 'Advanced communication skills training including active listening, articulation, tone modulation, and professional language mastery.',
      icon: Mic,
      color: 'from-cyan-500 to-teal-600',
      bgColor: 'bg-gradient-to-br from-cyan-50 to-teal-50 dark:from-cyan-950/30 dark:to-teal-950/30',
      borderColor: 'border-cyan-200 dark:border-cyan-800',
      path: null,
      statsLabel: 'Coming Soon',
      statsValue: '-',
      progress: null,
      features: ['Active Listening', 'Articulation', 'Tone Modulation', 'Professional Language'],
      badge: null,
      enabled: false,
      comingSoon: true
    }
  ];

  // Quick actions
  const quickActions = [
    { label: 'Evolve History', icon: History, path: '/evolve/history', desc: 'View past sessions' },
    { label: 'Performance', icon: TrendingUp, path: '/evolve', desc: 'Track your progress' }
  ];

  if (loading) {
    return (
      <MainLayout>
        <div className="p-6 space-y-6">
          <Skeleton className="h-12 w-64" />
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {[1, 2, 3].map(i => <Skeleton key={i} className="h-24" />)}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[1, 2].map(i => <Skeleton key={i} className="h-64" />)}
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="p-6 space-y-8" data-testid="communication-training-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-orange-600 via-cyan-600 to-teal-600 bg-clip-text text-transparent">
              Communication Training
            </h1>
            <p className="text-muted-foreground mt-1">
              {isAdmin 
                ? 'Manage communication and presentation training modules'
                : 'Master professional communication and presentation skills'
              }
            </p>
          </div>
          {isAdmin && (
            <Button variant="outline" onClick={() => navigate('/evolve/analytics')}>
              <BarChart3 className="h-4 w-4 mr-2" />
              View Analytics
            </Button>
          )}
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-950/30 dark:to-amber-950/30 border-orange-200 dark:border-orange-800">
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center gap-2">
                <Video className="h-5 w-5 text-orange-600" />
                <div>
                  <p className="text-xl font-bold">{stats.evolve.sessions}</p>
                  <p className="text-xs text-muted-foreground">Evolve Sessions</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-cyan-50 to-teal-50 dark:from-cyan-950/30 dark:to-teal-950/30 border-cyan-200 dark:border-cyan-800">
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center gap-2">
                <Mic className="h-5 w-5 text-cyan-600" />
                <div>
                  <p className="text-xl font-bold">-</p>
                  <p className="text-xs text-muted-foreground">Coach Sessions</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-purple-50 to-violet-50 dark:from-purple-950/30 dark:to-violet-950/30 border-purple-200 dark:border-purple-800">
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center gap-2">
                <Award className="h-5 w-5 text-purple-600" />
                <div>
                  <p className="text-xl font-bold">{stats.evolve.avgScore > 0 ? `${stats.evolve.avgScore}%` : '-'}</p>
                  <p className="text-xs text-muted-foreground">Avg Score</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-emerald-50 to-green-50 dark:from-emerald-950/30 dark:to-green-950/30 border-emerald-200 dark:border-emerald-800">
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-emerald-600" />
                <div>
                  <p className="text-xl font-bold">2</p>
                  <p className="text-xs text-muted-foreground">Modules</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Communication Modules Grid */}
        <div>
          <h2 className="text-xl font-semibold mb-4">Training Modules</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {communicationModules.map((module) => {
              const Icon = module.icon;
              const isDisabled = !module.enabled;
              return (
                <Card 
                  key={module.id}
                  className={`relative overflow-hidden transition-all ${
                    isDisabled 
                      ? 'opacity-60 cursor-not-allowed' 
                      : 'hover:shadow-lg hover:-translate-y-1 cursor-pointer'
                  } group ${module.bgColor} ${module.borderColor}`}
                  onClick={() => !isDisabled && module.path && navigate(module.path)}
                  data-testid={`communication-module-${module.id}`}
                >
                  {/* Coming Soon Overlay */}
                  {module.comingSoon && (
                    <div className="absolute inset-0 bg-white/60 dark:bg-black/40 z-10 flex items-center justify-center">
                      <Badge className="bg-slate-600 text-white text-sm px-4 py-1.5">
                        <Lock className="h-4 w-4 mr-2" />
                        Coming Soon
                      </Badge>
                    </div>
                  )}
                  
                  {/* Gradient Banner */}
                  <div className={`h-1.5 bg-gradient-to-r ${module.color}`} />
                  
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div className={`p-3 rounded-xl bg-gradient-to-br ${module.color} text-white shadow-lg ${!isDisabled && 'group-hover:scale-110'} transition-transform`}>
                        <Icon className="h-6 w-6" />
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        {module.badge && (
                          <Badge className="bg-emerald-500 text-white text-xs">
                            {module.badge}
                          </Badge>
                        )}
                        <Badge variant="secondary" className="text-xs">
                          {module.statsValue} {module.statsLabel}
                        </Badge>
                      </div>
                    </div>
                    <CardTitle className={`text-lg mt-3 ${!isDisabled && 'group-hover:text-primary'} transition-colors`}>
                      {module.title}
                    </CardTitle>
                    <CardDescription className="text-sm line-clamp-2">
                      {module.description}
                    </CardDescription>
                  </CardHeader>
                  
                  <CardContent className="space-y-4">
                    {/* Features */}
                    <div className="flex flex-wrap gap-1.5">
                      {module.features.slice(0, 4).map((feature, i) => (
                        <Badge key={i} variant="outline" className="text-xs font-normal bg-white/50 dark:bg-black/20">
                          {feature}
                        </Badge>
                      ))}
                      {module.features.length > 4 && (
                        <Badge variant="outline" className="text-xs font-normal bg-white/50 dark:bg-black/20">
                          +{module.features.length - 4}
                        </Badge>
                      )}
                    </div>
                    
                    {/* CTA */}
                    <div className="flex items-center justify-between pt-2 border-t border-border/50">
                      <span className={`text-sm font-medium ${isDisabled ? 'text-muted-foreground' : 'text-primary'}`}>
                        {isDisabled ? 'Coming Soon' : 'Start Training'}
                      </span>
                      {!isDisabled && (
                        <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all" />
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Quick Actions */}
        <div>
          <h2 className="text-xl font-semibold mb-4">Quick Actions</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {quickActions.map((action, i) => {
              const Icon = action.icon;
              return (
                <Card 
                  key={i}
                  className="hover:shadow-md transition-all cursor-pointer group hover:border-primary/50"
                  onClick={() => navigate(action.path)}
                  data-testid={`quick-action-${action.label.toLowerCase().replace(' ', '-')}`}
                >
                  <CardContent className="pt-4 pb-3 flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-muted group-hover:bg-primary/10 transition-colors">
                      <Icon className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
                    </div>
                    <div>
                      <p className="font-medium text-sm">{action.label}</p>
                      <p className="text-xs text-muted-foreground">{action.desc}</p>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Info Banner */}
        <div className="bg-gradient-to-r from-orange-50 to-cyan-50 dark:from-orange-950/30 dark:to-cyan-950/30 border border-orange-200 dark:border-orange-800 rounded-lg p-4">
          <p className="text-sm text-orange-800 dark:text-orange-200">
            <strong>Communication Training</strong> helps you master professional communication skills essential for recruitment success. 
            Practice presentations with <strong>Evolve AI</strong>, and stay tuned for our <strong>Communication Coach</strong> 
            for advanced voice and listening skills training.
          </p>
        </div>

        {/* Admin Section */}
        {isAdmin && (
          <Card className="bg-gradient-to-r from-slate-50 to-gray-50 dark:from-slate-950/50 dark:to-gray-950/50 border-slate-200 dark:border-slate-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Settings className="h-5 w-5" />
                Administration
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Button 
                  variant="outline" 
                  className="justify-start h-auto py-3"
                  onClick={() => navigate('/evolve/analytics')}
                >
                  <BarChart3 className="h-5 w-5 mr-3" />
                  <div className="text-left">
                    <p className="font-medium">Evolve Analytics</p>
                    <p className="text-xs text-muted-foreground">View presentation metrics</p>
                  </div>
                </Button>
                <Button 
                  variant="outline" 
                  className="justify-start h-auto py-3"
                  onClick={() => navigate('/settings')}
                >
                  <Settings className="h-5 w-5 mr-3" />
                  <div className="text-left">
                    <p className="font-medium">Module Settings</p>
                    <p className="text-xs text-muted-foreground">Configure Evolve AI settings</p>
                  </div>
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </MainLayout>
  );
};

export default CommunicationTraining;
